import {
  CollapsibleSectionCard,
  SectionHeader,
  NumericField,
} from "../../../components/shared/EditorShared";
import type { BundleEditorItem } from "../../../store/bundle-editor.mapping";
import {
  openBundleResourcePicker,
  type BundleVariantPick,
} from "../../../utils/resource-picker.utils";

// ─── Section 3a: Fixed items ──────────────────────────────────────────────────

export function FixedItemsSection({
  items,
  onAdd,
  onRemove,
  onUpdate,
}: {
  items: BundleEditorItem[];
  onAdd: (picks: BundleVariantPick[]) => void;
  onRemove: (index: number) => void;
  onUpdate: <K extends keyof BundleEditorItem>(
    index: number,
    key: K,
    value: BundleEditorItem[K]
  ) => void;
}) {
  const handlePick = () => {
    openBundleResourcePicker(
      (picks) => onAdd(picks),
      () => {
        window.shopify?.toast?.show(
          "No se pudo abrir el selector de productos.",
          { isError: true }
        );
      }
    );
  };

  return (
    <CollapsibleSectionCard
      header={
        <SectionHeader number={4} title="Productos del bundle" required />
      }
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          Selecciona los productos y variantes que forman este bundle. Define la
          cantidad de cada uno.
        </s-text>

        {items.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 flex flex-col items-center gap-3 text-center">
            <s-icon type="product" />
            <s-text color="subdued">No has agregado productos todavía.</s-text>
            <s-button variant="primary" icon="plus" onClick={handlePick}>
              Seleccionar productos
            </s-button>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-[48px_1fr_120px_40px] bg-gray-50 border-b border-gray-200 px-3 py-2 items-center">
              <s-text type="strong" />
              <s-text type="strong">Producto</s-text>
              <s-text type="strong">Cantidad</s-text>
              <s-text type="strong" />
            </div>

            {items.map((item, i) => (
              <div
                key={item._tempId}
                className="grid grid-cols-[48px_1fr_120px_40px] items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-b-0"
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="w-10 h-10 rounded object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                    <s-icon type="image" />
                  </div>
                )}

                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.productTitle || "Producto"}
                  </p>
                  {item.variantTitle && (
                    <p className="text-xs text-gray-500 truncate">
                      {item.variantTitle}
                    </p>
                  )}
                </div>

                <NumericField
                  label="Cantidad"
                  value={item.quantity}
                  min={1}
                  onChange={(v) => onUpdate(i, "quantity", v)}
                />

                <button
                  onClick={() => onRemove(i)}
                  className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Quitar producto"
                >
                  <s-icon type="delete" />
                </button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <s-button variant="tertiary" icon="plus" onClick={handlePick}>
            Agregar más productos
          </s-button>
        )}
      </s-stack>
    </CollapsibleSectionCard>
  );
}
